import React from "react";
import PropTypes from "prop-types";
import { Box, Chip, makeStyles } from "@material-ui/core";
import TypographyArabic from "../../../../../SharedComponents/TypographyArabic.jsx";
import LearnAudioPlayer from "./LearnAudioPlayer.jsx";
import formatTextSegment from "./formatTextSegment.js";
import parseGapVariants from "./parseGapVariants.js";

const useStyles = makeStyles(() => ({
  // Длинные правильные ответы переносятся целиком, чтобы ученик мог прочитать их без обрезки.
  textAnswer: {
    margin: 4,
    height: "auto",
    "& .MuiChip-label": {
      whiteSpace: "normal",
      padding: "8px 12px",
    },
  },
  pairValue: {
    width: "50%",
    minWidth: 0,
    padding: 8,
    overflowWrap: "anywhere",
  },
}));

/**
 * Заполняет пропуски правильными ответами.
 *
 * @param {string} text - Текст упражнения с пропусками в фигурных скобках.
 * @param {boolean} markedAnswers - Требует отметки звёздочкой у правильных вариантов.
 * @returns {string} Заполненный текст с сохранёнными переносами строк и маркерами форматирования.
 */
const getCorrectAnswerText = (text, markedAnswers) =>
  text.replace(/{([^{}]+)}/g, (_match, gap) => {
    const correctVariants = parseGapVariants(gap, markedAnswers)
      .filter((variant) => variant.right)
      .map((variant) => variant.text);
    return correctVariants.length > 1 ? `(${correctVariants.join(" / ")})` : correctVariants.join("");
  });

/**
 * Показывает правильный ответ без проверки ответа ученика и изменения результатов экзамена.
 * Поддерживает те же типы упражнений, что и окно просмотра ответа в Android.
 *
 * @param {Object} exercise - Упражнение с типом и данными правильного ответа.
 * @returns {React.ReactNode} Текст, картинки или аудио правильного ответа.
 */
const CorrectAnswer = ({ exercise }) => {
  const classes = useStyles();
  const answer = exercise.data.answer || exercise.data;

  switch (exercise.type) {
    case 3:
      return (
        <TypographyArabic dir={answer.rtl ? "rtl" : "ltr"}>
          {(answer.rtl ? [...answer.sentence].reverse() : answer.sentence).join(" ")}
        </TypographyArabic>
      );
    case 5:
      return <TypographyArabic>{answer.words.join(", ")}</TypographyArabic>;
    case 8:
      return (
        <Box width="100%">
          {answer.pairs.map((pair, index) => (
            <Box key={index} display="flex" alignItems="center" data-tag="correct pair">
              <Box className={classes.pairValue}>
                {answer.pairType === "image-text" ? (
                  <img src={pair[0]} alt="" style={{ maxWidth: "100%", maxHeight: 120, objectFit: "contain" }} />
                ) : (
                  <TypographyArabic>{pair[0]}</TypographyArabic>
                )}
              </Box>
              <Box className={classes.pairValue}>
                <TypographyArabic>{pair[1]}</TypographyArabic>
              </Box>
            </Box>
          ))}
        </Box>
      );
    case 9:
    case 13:
      return (
        <Box width="100%" whiteSpace="pre-wrap" dir={answer.rtl ? "rtl" : "ltr"}>
          {formatTextSegment(getCorrectAnswerText(answer.text, exercise.type === 13)).nodes}
        </Box>
      );
    case 11:
      switch (answer.type) {
        case "keyboard":
          return <TypographyArabic>{answer.keyboard}</TypographyArabic>;
        case "select_text":
          return (
            <Box display="flex" flexWrap="wrap" justifyContent="center">
              {answer.selectText
                .filter((item) => item.right)
                .map((item, index) => (
                  <Chip
                    key={index}
                    variant="outlined"
                    label={formatTextSegment(item.answer).nodes}
                    className={classes.textAnswer}
                  />
                ))}
            </Box>
          );
        case "select_image":
          return answer.selectImage
            .filter((item) => item.right)
            .map((item, index) => (
              <img
                key={index}
                src={item.answer.url}
                alt=""
                style={{ maxWidth: "100%", maxHeight: 300, objectFit: "contain", margin: "5px 0" }}
              />
            ));
        case "select_audio":
          return answer.selectAudio
            .filter((item) => item.right)
            .map((item, index) => (
              <Box key={index} display="flex" alignItems="center" flexWrap="wrap" justifyContent="center" my={1}>
                <LearnAudioPlayer url={item.answer.url} />
              </Box>
            ));
        default:
          return null;
      }
    default:
      return null;
  }
};

CorrectAnswer.propTypes = {
  exercise: PropTypes.object.isRequired,
};

export default CorrectAnswer;
